import env from '../config/env';
import CancelCreditEligible from '../interfaces/dto/cancelCreditEligibleDTO';
import type { CadenceType, ProductsSelfData } from '../interfaces/subscriptions';
import { get, post } from '../utils/api';
import { apiPaths } from '../utils/apiPaths';

interface GrowthSubscriptionMedia {
  isThumbnail?: boolean;
  url?: string;
}

interface GrowthSubscription {
  id?: number | string;
  productId?: number | string;
  variantId?: number | string;
  type?: string;
  cadence?: string;
  active?: boolean;
  nonrenew?: boolean;
  cancelled?: boolean;
  brandName?: string;
  name?: string;
  slug?: string;
  medias?: GrowthSubscriptionMedia[];
  imageUrl?: string;
  thumbnailUrl?: string;
  price?: number;
  nextBillDate?: string | null;
  quantity?: number;
  subscriptionLimit?: number;
}

interface GrowthSubscriptionsResponse {
  subscriptions?: GrowthSubscription[];
}

const GO_WITH_BOX_CADENCES = new Set(['GO_WITH_BOX', 'SWB']);
const NINETY_DAYS_CADENCES = new Set(['NINETY_DAYS', 'P3M']);

const toNumber = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = Number(value);
    if (!Number.isNaN(parsedValue)) {
      return parsedValue;
    }
  }

  return fallback;
};

const getThumbnailUrl = (subscription: GrowthSubscription): string =>
  subscription.medias?.find((media) => media?.isThumbnail)?.url ||
  subscription.thumbnailUrl ||
  subscription.imageUrl ||
  '';

const getCadenceType = (cadence: string): CadenceType | null => {
  if (GO_WITH_BOX_CADENCES.has(cadence)) {
    return 'GO_WITH_BOX';
  }
  if (NINETY_DAYS_CADENCES.has(cadence)) {
    return 'NINETY_DAYS';
  }
  return null;
};

const isActiveProductSubscription = (subscription: GrowthSubscription): boolean => {
  const isProduct = subscription.type ? subscription.type === 'PRODUCT' : true;
  return isProduct && !!subscription.active && !subscription.nonrenew && !subscription.cancelled;
};

const mapSubscriptionItem = (
  subscription: GrowthSubscription,
  index: number,
  cadenceType: CadenceType,
) => {
  const fallbackProductId = index + 1;
  const productId = toNumber(subscription.productId ?? subscription.id, fallbackProductId);
  const variantId = toNumber(subscription.variantId ?? productId, productId);
  const replenishmentPrice = subscription.price ?? 0;

  return {
    availableSubscriptions: subscription.subscriptionLimit ?? 0,
    subscriptionTotal: replenishmentPrice,
    product: {
      id: productId,
      name: subscription.name || '',
      brand: subscription.brandName || '',
      slug: subscription.slug || '',
      variants: [
        {
          id: variantId,
          productId,
          imageUrl: getThumbnailUrl(subscription),
          sku: '',
          description: '',
          sizeInfo: null,
          availableInventory: 0,
          replenishmentPrice,
          retailPrice: replenishmentPrice,
          subscriptionsLimit: subscription.subscriptionLimit ?? 0,
          displayOrder: 0,
          deleted: false,
          shippingRestrictions: null,
          nextVariantInChain: null,
          replacementVariantId: null,
          psProductId: productId,
        },
      ],
      psProductId: productId,
    },
    subscription: {
      variantId,
      campaignId: null,
      quantity: subscription.quantity ?? 1,
      quantityChange: null,
      billedQuantityChange: null,
      sku: null,
      cadenceType,
      liveInCampaignType: '',
      nextRenewalDate: subscription.nextBillDate ?? null,
      billDate: subscription.nextBillDate ?? null,
    },
  };
};

const buildCadenceDetails = (
  cadenceType: CadenceType,
  subscriptions: ReturnType<typeof mapSubscriptionItem>[],
) => ({
  cadenceType,
  shippingFeeInCents: 0,
  processingFeeInCents: 0,
  subtotalInCents: 0,
  nextRenewalDate: '',
  nextRenewalSeason: null,
  subscriptions,
});

const mapGrowthSubscriptionsToProductsSelfData = (
  subscriptions: GrowthSubscription[],
): ProductsSelfData => {
  const mappedSubscriptions = subscriptions.filter(isActiveProductSubscription).reduce(
    (acc, subscription, index) => {
      const cadenceType = getCadenceType(subscription.cadence || '');
      if (!cadenceType) {
        return acc;
      }
      acc[cadenceType].push(mapSubscriptionItem(subscription, index, cadenceType));
      return acc;
    },
    {
      GO_WITH_BOX: [] as ReturnType<typeof mapSubscriptionItem>[],
      NINETY_DAYS: [] as ReturnType<typeof mapSubscriptionItem>[],
    },
  );

  const activeProductSubscriptions: ProductsSelfData['activeProductSubscriptions'] = {};

  if (mappedSubscriptions.GO_WITH_BOX.length > 0) {
    activeProductSubscriptions.GO_WITH_BOX = buildCadenceDetails(
      'GO_WITH_BOX',
      mappedSubscriptions.GO_WITH_BOX,
    );
  }

  if (mappedSubscriptions.NINETY_DAYS.length > 0) {
    activeProductSubscriptions.NINETY_DAYS = buildCadenceDetails(
      'NINETY_DAYS',
      mappedSubscriptions.NINETY_DAYS,
    );
  }

  return { activeProductSubscriptions };
};

const getCancelCreditEligible = async (): Promise<CancelCreditEligible> => {
  const url = apiPaths.getCancelCreditEligible;
  return get(env.GROWTH_API_URL || 'https://growth-api.fabfitfun.com', url).then(
    (response) => response.data,
  );
};

const getProductsSelfData = async (): Promise<ProductsSelfData> => {
  const url = apiPaths.getProductsSelfData;
  return get(env.GROWTH_API_URL || 'https://growth-api.fabfitfun.com', url).then((response) =>
    mapGrowthSubscriptionsToProductsSelfData(
      (response.data as GrowthSubscriptionsResponse)?.subscriptions || [],
    ),
  );
};

const postCancelCredit = async (): Promise<void> => {
  const url = apiPaths.postCancelCredit;
  return post(env.GROWTH_API_URL || 'https://growth-api.fabfitfun.com', {}, url).then(
    (response) => response.data,
  );
};

export { getCancelCreditEligible, getProductsSelfData, postCancelCredit };
